import { TransitionKind, TransitionSpeed } from "@repo/api/transition";
import { interpolate } from "remotion";
import { easeInSine } from "../../utils/easing.ts";
import { msToFrames } from "../../utils/timing.ts";
import {
  COMPOSITION_FADE_TRANSITION_DURATIONS_MS,
  CompositionTransitionBuilder,
  CompositionTransitionWrapperProps,
  getDefaultPreviewClipDurationInFrames,
} from "./CompositionTransitionBuilder.ts";

const transitionKind = TransitionKind.enum.FADE_WHITE;

export const StartFadeWhiteTransitionBuilder: CompositionTransitionBuilder = {
  formConfig: {
    kind: transitionKind,
    buildDefaultValue: () => ({ kind: transitionKind, speed: "NORMAL" }),
    speed: ["SLOW", "NORMAL", "FAST"],
  },
  wrapper: {
    overlayComponent: StartOverlay,
  },
  getPreviewClipDurationInFrames: getDefaultPreviewClipDurationInFrames,
};

export const EndFadeWhiteTransitionBuilder: CompositionTransitionBuilder = {
  formConfig: {
    kind: transitionKind,
    buildDefaultValue: () => ({ kind: transitionKind, speed: "NORMAL" }),
    speed: ["SLOW", "NORMAL", "FAST"],
  },
  wrapper: {
    overlayComponent: EndOverlay,
  },
  getPreviewClipDurationInFrames: getDefaultPreviewClipDurationInFrames,
};

function getDurationInFrames(speed: TransitionSpeed | undefined, fps: number) {
  if (!speed) {
    return null;
  }

  return msToFrames(COMPOSITION_FADE_TRANSITION_DURATIONS_MS[speed], fps);
}

function StartOverlay({
  fps,
  currentFrame,
  transition,
}: CompositionTransitionWrapperProps) {
  const endFrame = getDurationInFrames(transition.speed, fps);

  if (!endFrame) {
    return null;
  }

  const startOpacity = interpolate(currentFrame, [0, endFrame], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInSine,
  });

  if (currentFrame > endFrame) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 bg-white"
      style={{ opacity: startOpacity }}
    ></div>
  );
}

function EndOverlay({
  fps,
  currentFrame,
  transition,
  durationInFrames,
}: CompositionTransitionWrapperProps) {
  const transitionDurationFrames = getDurationInFrames(transition.speed, fps);

  if (!transitionDurationFrames) {
    return null;
  }

  const endFrame = Math.max(1, durationInFrames - 1);
  const startFrame = Math.max(0, endFrame - transitionDurationFrames);

  const endOpacity = interpolate(currentFrame, [startFrame, endFrame], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: easeInSine,
  });

  if (currentFrame < startFrame) {
    return null;
  }

  return (
    <div
      className="absolute inset-0 bg-white"
      style={{ opacity: endOpacity }}
    ></div>
  );
}
